import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Mic, Square, Play, RotateCcw, Send, CheckCircle2, Loader2, Music, MessageSquare, Heart, Cake, HelpCircle, Trophy, Lock, MessageCircle, Mail } from "lucide-react";

const B = process.env.REACT_APP_BACKEND_URL;
const TYPE_ICONS = { song_request: Music, shoutout: MessageSquare, dedication: Heart, birthday: Cake, ask_dj: HelpCircle, contest: Trophy, confession: Lock, feedback: MessageCircle, general: Mail };

export default function PublicVoicePage() {
  const { stationId } = useParams();
  const [cfg, setCfg] = useState(null);
  const [missing, setMissing] = useState(false);
  const [cat, setCat] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | recording | review | sending | done
  const [secs, setSecs] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [form, setForm] = useState({ name: "", email: "", location: "", show: "" });
  const [extra, setExtra] = useState({ dedication_song: "", dedicated_to: "", birthday_person: "", birthday_date: "", contest_id: "" });
  const embed = new URLSearchParams(window.location.search).get("embed") === "1";
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${B}/api/voice/public/${stationId}/config`)
      .then((r) => { setCfg(r.data); setCat(r.data.categories?.[0]?.key || "general"); })
      .catch(() => setMissing(true));
    return () => clearInterval(timerRef.current);
  }, [stationId]);

  if (missing) return <div className="min-h-screen bg-[#0a0b10] flex items-center justify-center text-white/50 text-sm">Station not found.</div>;
  if (!cfg) return <div className="min-h-screen bg-[#0a0b10] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>;
  const accent = cfg.accent_color || "#fb923c";
  const maxS = cfg.max_recording_seconds || 60;

  const startRec = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
        setPhase("review");
      };
      mrRef.current = mr;
      mr.start();
      setSecs(0);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        setSecs((s) => {
          if (s + 1 >= maxS) { stopRec(); return maxS; }
          return s + 1;
        });
      }, 1000);
    } catch {
      setErr("No microphone detected — allow mic access and try again.");
    }
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    if (mrRef.current?.state === "recording") mrRef.current.stop();
  };

  const reset = () => { setBlob(null); setPreviewUrl(""); setSecs(0); setPhase("idle"); };

  const submit = async () => {
    if (!form.name.trim()) { setErr("Please tell us your first name or nickname."); return; }
    if (!consent) { setErr("Please tick the consent box so we can play your message."); return; }
    setPhase("sending"); setErr("");
    try {
      const fd = new FormData();
      const ext = (blob.type || "").includes("mp4") ? ".mp4" : (blob.type || "").includes("ogg") ? ".ogg" : ".webm";
      fd.append("audio", blob, `clip${ext}`);
      fd.append("name", form.name); fd.append("email", form.email);
      fd.append("location", form.location); fd.append("show", form.show);
      fd.append("submission_type", cat); fd.append("duration", String(secs));
      fd.append("consent", "true");
      Object.entries(extra).forEach(([k, v]) => v && fd.append(k, v));
      await axios.post(`${B}/api/voice/public/${stationId}/submit`, fd);
      setPhase("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send — please try again.");
      setPhase("review");
    }
  };

  return (
    <div className={`min-h-screen bg-[#0a0b10] text-white flex items-start justify-center ${embed ? "p-1" : "p-4 sm:p-10"}`} data-testid="public-voice-page">
      <div className={`w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-7 ${embed ? "" : "mt-4"}`}>
        <div className="text-center">
          {cfg.logo_url ? (
            <img src={`${B}${cfg.logo_url}`} alt="logo" className="h-16 w-16 rounded-full object-cover mx-auto border border-white/15" />
          ) : (
            <div className="h-16 w-16 rounded-full mx-auto" style={{ background: `linear-gradient(135deg, ${accent}, #f43f5e)` }} />
          )}
          <h1 className="mt-4 text-xl font-bold" data-testid="voice-page-title">Send Us Your Voice</h1>
          <p className="mt-1 text-xs text-white/55">{cfg.welcome_message} <span className="text-white/70 font-semibold">{cfg.station_name}</span>.</p>
        </div>

        {phase === "done" ? (
          <div className="mt-8 text-center" data-testid="voice-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <div className="mt-3 text-lg font-bold">Message sent! 🎙️</div>
            <p className="mt-1 text-xs text-white/55">The station team will review it — listen out, you might hear yourself on air.</p>
            <button onClick={() => { reset(); setForm({ name: "", email: "", location: "", show: "" }); setConsent(false); }}
              data-testid="voice-send-another"
              className="mt-5 rounded-full px-5 py-2 text-xs font-bold text-black" style={{ background: accent }}>
              Send another message
            </button>
          </div>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-2 gap-2">
              {(cfg.categories || []).map((c) => {
                const Icon = TYPE_ICONS[c.key] || Mail;
                const on = cat === c.key;
                return (
                  <button key={c.key} onClick={() => setCat(c.key)} data-testid={`voice-cat-${c.key}`}
                    className={`inline-flex items-center gap-1.5 justify-center rounded-full border px-3 py-2 text-xs font-semibold transition ${on ? "text-black" : "border-white/15 text-white/70 hover:border-white/30"}`}
                    style={on ? { background: accent, borderColor: accent } : {}}>
                    <Icon className="h-3.5 w-3.5" /> {c.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 text-center">
              {phase === "idle" && (
                <>
                  <button onClick={startRec} data-testid="voice-record-btn"
                    className="h-20 w-20 rounded-full mx-auto flex items-center justify-center transition hover:scale-105"
                    style={{ background: "linear-gradient(135deg, #f43f5e, #e11d48)" }}>
                    <Mic className="h-8 w-8 text-white" />
                  </button>
                  <div className="mt-3 text-[11px] text-white/45">Tap → Record → Listen → Send<br />Max {maxS} seconds</div>
                </>
              )}
              {phase === "recording" && (
                <>
                  <button onClick={stopRec} data-testid="voice-stop-btn"
                    className="h-20 w-20 rounded-full mx-auto flex items-center justify-center animate-pulse"
                    style={{ background: "#e11d48" }}>
                    <Square className="h-7 w-7 text-white fill-white" />
                  </button>
                  <div className="mt-3 font-mono text-lg font-bold" style={{ color: accent }} data-testid="voice-timer">
                    0:{String(secs).padStart(2, "0")} <span className="text-white/40 text-xs">/ 0:{String(maxS).padStart(2, "0")}</span>
                  </div>
                  <div className="text-[11px] text-white/45">Recording… tap to stop</div>
                </>
              )}
              {(phase === "review" || phase === "sending") && (
                <div className="space-y-3 text-left" data-testid="voice-review">
                  <audio controls src={previewUrl} className="w-full" data-testid="voice-preview-audio" />
                  <div className="flex justify-center">
                    <button onClick={reset} data-testid="voice-rerecord-btn"
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/60 hover:text-white">
                      <RotateCcw className="h-3.5 w-3.5" /> Re-record
                    </button>
                  </div>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="First name or nickname *" data-testid="voice-name"
                    className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" maxLength={60} />
                  {cat === "dedication" && (
                    <div className="grid grid-cols-2 gap-2" data-testid="voice-dedication-fields">
                      <input value={extra.dedication_song} onChange={(e) => setExtra({ ...extra, dedication_song: e.target.value })}
                        placeholder="Song to dedicate" data-testid="voice-dedication-song"
                        className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" maxLength={120} />
                      <input value={extra.dedicated_to} onChange={(e) => setExtra({ ...extra, dedicated_to: e.target.value })}
                        placeholder="Dedicated to" data-testid="voice-dedicated-to"
                        className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" maxLength={80} />
                    </div>
                  )}
                  {cat === "birthday" && (
                    <div className="grid grid-cols-2 gap-2" data-testid="voice-birthday-fields">
                      <input value={extra.birthday_person} onChange={(e) => setExtra({ ...extra, birthday_person: e.target.value })}
                        placeholder="Whose birthday?" data-testid="voice-birthday-person"
                        className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" maxLength={80} />
                      <input type="date" value={extra.birthday_date} onChange={(e) => setExtra({ ...extra, birthday_date: e.target.value })}
                        data-testid="voice-birthday-date"
                        className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" />
                    </div>
                  )}
                  {cat === "contest" && (cfg.active_contests || []).length > 0 && (
                    <select value={extra.contest_id} onChange={(e) => setExtra({ ...extra, contest_id: e.target.value })}
                      data-testid="voice-contest-select"
                      className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm">
                      <option value="">Which contest are you entering?</option>
                      {cfg.active_contests.map((c) => <option key={c.contest_id} value={c.contest_id}>{c.title}</option>)}
                    </select>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email (optional)" type="email" data-testid="voice-email"
                      className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" />
                    <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                      placeholder="City (optional)" data-testid="voice-location"
                      className="rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" maxLength={80} />
                  </div>
                  <input value={form.show} onChange={(e) => setForm({ ...form, show: e.target.value })}
                    placeholder="Show or DJ it's for (optional)" data-testid="voice-show"
                    className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm" maxLength={80} />
                  <label className="flex items-start gap-2 text-[11px] text-white/55 cursor-pointer">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      data-testid="voice-consent" className="mt-0.5" />
                    I understand that this recording may be edited and broadcast by the radio station.
                  </label>
                  <button onClick={submit} disabled={phase === "sending"} data-testid="voice-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold text-black disabled:opacity-60"
                    style={{ background: accent }}>
                    {phase === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {phase === "sending" ? "Sending…" : "Send to the station"}
                  </button>
                </div>
              )}
              {err && <div className="mt-3 text-xs text-rose-300" data-testid="voice-error">{err}</div>}
            </div>
            <p className="mt-6 text-[10px] text-white/30 text-center">
              We only collect what you enter above. Recordings are reviewed by station staff and never air automatically.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
